export const MIN_SCALE = 1;
export const MAX_SCALE = 5;
export const BUTTON_SCALE_STEP = 0.5;
export const WHEEL_SCALE_SENSITIVITY = 0.0015;

export function clampScale(scale: number, min: number = MIN_SCALE, max: number = MAX_SCALE): number {
  return Math.min(max, Math.max(min, scale));
}

// Multiplicative (not additive) scaling feels natural across both discrete mouse-wheel
// ticks and continuous trackpad deltas.
export function nextScaleFromWheelDelta(
  currentScale: number,
  deltaY: number,
  sensitivity: number = WHEEL_SCALE_SENSITIVITY,
  min: number = MIN_SCALE,
  max: number = MAX_SCALE,
): number {
  const factor = 1 - deltaY * sensitivity;
  return clampScale(currentScale * factor, min, max);
}

export function nextScaleFromStep(
  currentScale: number,
  direction: 1 | -1,
  step: number = BUTTON_SCALE_STEP,
  min: number = MIN_SCALE,
  max: number = MAX_SCALE,
): number {
  return clampScale(currentScale + direction * step, min, max);
}

export interface Point {
  x: number;
  y: number;
}

export interface ContainerSize {
  w: number;
  h: number;
}

// `renderedImage` is the image's own unscaled (pre-transform) box. A letterboxed axis
// (rendered smaller than the container) keeps its max pan at 0 until scaling it up
// exceeds the container.
export function clampPan(
  pan: Point,
  scale: number,
  renderedImage: ContainerSize,
  container: ContainerSize,
  min: number = MIN_SCALE,
): Point {
  if (scale <= min) {
    return { x: 0, y: 0 };
  }
  const maxX = Math.max(0, (renderedImage.w * scale - container.w) / 2);
  const maxY = Math.max(0, (renderedImage.h * scale - container.h) / 2);
  return {
    x: Math.min(maxX, Math.max(-maxX, pan.x)),
    y: Math.min(maxY, Math.max(-maxY, pan.y)),
  };
}

/** Fraction of the container's width (left/right) or height (up/down) moved by one pan button press or arrow key. */
export const PAN_STEP_FRACTION = 0.1;

export type PanDirection = 'up' | 'down' | 'left' | 'right';

// Directions name the part of the image brought into view, so panning up moves the image down.
const PAN_VECTORS: Record<PanDirection, Point> = {
  up: { x: 0, y: 1 },
  down: { x: 0, y: -1 },
  left: { x: 1, y: 0 },
  right: { x: -1, y: 0 },
};

export function panByStep(
  pan: Point,
  direction: PanDirection,
  scale: number,
  renderedImage: ContainerSize,
  container: ContainerSize,
  min: number = MIN_SCALE,
): Point {
  const vector = PAN_VECTORS[direction];
  const next = {
    x: pan.x + vector.x * container.w * PAN_STEP_FRACTION,
    y: pan.y + vector.y * container.h * PAN_STEP_FRACTION,
  };
  return clampPan(next, scale, renderedImage, container, min);
}

/** Whether one step in `direction` would move the view, i.e. it isn't already at that edge. */
export function canPan(
  pan: Point,
  direction: PanDirection,
  scale: number,
  renderedImage: ContainerSize,
  container: ContainerSize,
  min: number = MIN_SCALE,
): boolean {
  const next = panByStep(pan, direction, scale, renderedImage, container, min);
  return next.x !== pan.x || next.y !== pan.y;
}
